import { createContext, useContext, useState, useCallback } from 'react';
import { Alert } from '../models/alert';

interface AlertContextType {
    alerts: Alert[];
    addAlert: (message: string, duration?: number, type?: 'info' | 'success' | 'warning' | 'error', dismissible?: boolean ) => string;
    removeAlert: (id: string) => void;
    clearAlerts: () => void;
    addError: (error: Error, prefix?: string) => void;
}

export const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: React.ReactNode }) {
    const [alerts, setAlerts] = useState<Alert[]>([]);

    const addAlert = useCallback((message: string, duration?: number, type?: 'info' | 'success' | 'warning' | 'error', dismissible?: boolean ) => {
        const id = crypto.randomUUID();

        if(type===undefined){
            type='info';
        }
        if(duration==undefined){
            duration=5000;
        }

        const newAlert: Alert = { id, message, type, dismissible };

        setAlerts((prev) => [...prev, newAlert]);

        // Auto-dismiss if duration is set and greater than 0
        if (duration > 0) {
            setTimeout(() => {
                removeAlert(id);
            }, duration);

            return id;
        }

        return id;
    }, []);

    const removeAlert = useCallback((id: string) => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    }, []);

    const clearAlerts = useCallback(() => {
        setAlerts([]);
    }, []);

    const addError = useCallback((error: Error, prefix?: string)=>{
        let message=error.message;
        if(prefix){
            message=prefix+": "+message;
        }
        addAlert(message,5000,'error',true);
    },[])

    return (
        <AlertContext.Provider value={{ alerts, addAlert, removeAlert, clearAlerts, addError }}>
            {children}
        </AlertContext.Provider>
    );
}

export function useAlerts() {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlerts must be used within an AlertProvider');
    }
    return context;
}
